import { LocalScheme } from '@nuxtjs/auth-next/dist/runtime';
import { getActivePinia } from 'pinia';

import { useIsomorphicContext } from '@/plugins/app-context';
import { clearCityOfSale } from '@/shared/composables';
import { EnvironmentType } from '@/shared/lib';
import { DataLayerService } from '@/shared/services';

let isMissingTopHostReported = false;

function resolveCookieDomain ({
  ENVIRONMENT,
  TOP_HOST,
}) {
  if (ENVIRONMENT === EnvironmentType.Local) {
    return '';
  }

  if (!TOP_HOST) {
    if (process.server && !isMissingTopHostReported) {
      isMissingTopHostReported = true;
      console.error('[auth] TOP_HOST is not set in runtime config, auth cookies are host-only');
    }

    return '';
  }

  return `.${ TOP_HOST }`;
}

export default class EmailScheme extends LocalScheme {
  constructor ($auth, options, ...defaults) {
    super($auth, options, ...defaults);

    Object.assign($auth.$storage.options.cookie.options, {
      domain: resolveCookieDomain($auth.ctx.$config),
      secure: $auth.ctx.$config.ENVIRONMENT !== EnvironmentType.Local,
      sameSite: 'lax',
    });
  }

  async login (endpoint, {
    reset = true,
  } = {}) {
    const response = await super.login(endpoint, {
      reset,
    });
    const {
      data,
    } = response;

    const roleBackend = {
      agent: 'agents',
      repres: 'represes',
    };

    if (!roleBackend[data.role]) {
      return;
    }

    await this.$auth.setStrategy(roleBackend[data.role]);
    await this.$auth.setUserToken(data.token);
    await this.$auth.fetchUser();

    const dataLayer = new DataLayerService();
    const strategy = this.$auth.$state.strategy;

    const roleSEO = {
      agents: 'agent',
      represes: 'agency',
      users: 'user',
    };

    dataLayer.setUserId(data.id);
    dataLayer.addEvent('login', {
      role: roleSEO[strategy],
    });

    return response;
  }

  resetStore () {
    const nuxtContext = useIsomorphicContext();

    getActivePinia()._s.forEach((store) => {
      if (['deals'].includes(store.$id)) {
        store.$dispose();
        delete nuxtContext.$pinia.state.value[store.$id];
      }
    });
  }

  async logout (endpoint) {
    const response = await super.logout(endpoint).then(() => {
      this.resetStore();
      clearCityOfSale();
    });

    if (process.env.DEVELOPMENT) {
      this.$auth.$storage.removeCookie('_lksession', {
        prefix: '',
        path: '/',
        domain: '',
      });
    }

    return response;
  }
}
